import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Card, Col, ColorPicker, Dropdown, Form, Input, Modal, Row, Select, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { AppstoreOutlined, DeleteOutlined, MoreOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { PROJECT_COLOR_KEYS, PROJECTS_MIN_ACTIVE, type ContactListItem, type ProjectColorKey, type ProjectItem, type ProjectOwnerItem } from '@crm/shared';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { NameCell } from '../../components/NameCell';
import { IMAGE_ACCEPT } from '../../components/PhotoAvatar';
import { EmptyState, tableLocale } from '../../components/states';
import { api, errorMessage, fileUrl } from '../../api/client';
import { palette } from '../../app/theme';
import { contactName } from '../../features/contacts/api';
import { useCreateProject, useDeleteProject, useProjectImage, useProjects, useUpdateProject, type ProjectImage } from '../../features/projects/api';
import { plural } from '../../utils/format';
import { QuickCreateModal } from '../../components/QuickCreateModal';
import { MentionTextArea } from '../../components/Mentions';

type ProjectForm = { name: string; acronym?: string | null; url?: string | null; description?: string | null; ownerIds?: string[] } & Partial<Record<ProjectColorKey, string | null>>;

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const hexRule = { pattern: HEX, message: 'Use a hex colour like #2F5BEA' };
/** "abc" / "#ABC" → "#aabbcc"; anything else is returned as typed (the field rule flags it). */
const normalizeHex = (v: string | null | undefined) => {
  const h = v?.trim().replace(/^#/, '') ?? '';
  if (!HEX.test(h)) return v?.trim() || null;
  return `#${(h.length === 3 ? h.replace(/./g, (c) => c + c) : h).toLowerCase()}`;
};
const BRAND_COLORS: { key: 'primaryColor' | 'secondaryColor' | 'accentColor'; label: string }[] = [
  { key: 'primaryColor', label: 'Primary' },
  { key: 'secondaryColor', label: 'Secondary' },
  { key: 'accentColor', label: 'Accent' },
];

const labelledCount = (p: ProjectItem) => p.counts.tags + p.counts.lists + p.counts.segments + p.counts.campaigns;

export function ProjectsPage() {
  const { message, modal } = App.useApp();
  const projects = useProjects();
  const create = useCreateProject();
  const update = useUpdateProject();
  const remove = useDeleteProject();
  const image = useProjectImage();
  const [editing, setEditing] = useState<ProjectItem | 'new' | null>(null);
  /** images picked for a project that is not saved yet; uploaded right after it is created */
  const [pending, setPending] = useState<Partial<Record<ProjectImage, File>>>({});
  const [form] = Form.useForm<ProjectForm>();
  const count = projects.data?.length ?? 0;
  const current = editing && editing !== 'new' ? (projects.data?.find((p) => p.id === editing.id) ?? editing) : null;

  const pickImage = async (kind: ProjectImage, file: File | null) => {
    if (!current) return setPending((p) => ({ ...p, [kind]: file ?? undefined }));
    const noun = kind === 'logo' ? 'Logo' : 'Icon';
    try {
      await image.mutateAsync({ id: current.id, kind, file });
      message.success(file ? `${noun} updated` : `${noun} removed`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const open = (p: ProjectItem | 'new') => {
    form.resetFields();
    setPending({});
    if (p !== 'new') form.setFieldsValue({ name: p.name, acronym: p.acronym, url: p.url, description: p.description, ownerIds: p.owners.map((o) => o.id), ...Object.fromEntries(PROJECT_COLOR_KEYS.map((k) => [k, p[k]])) });
    setEditing(p);
  };

  const save = async (v: ProjectForm) => {
    const input = { ...v, ownerIds: v.ownerIds ?? [] };
    try {
      if (editing === 'new') {
        const p = await create.mutateAsync(input);
        for (const kind of ['logo', 'icon'] as const) {
          const file = pending[kind];
          if (file) await image.mutateAsync({ id: p.id, kind, file });
        }
      } else if (editing) await update.mutateAsync({ id: editing.id, ...input });
      message.success(editing === 'new' ? 'Project created' : 'Project saved');
      setEditing(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const confirmDelete = (p: ProjectItem) => {
    const n = labelledCount(p);
    modal.confirm({
      title: `Delete project "${p.name}"?`,
      content: [
        n ? `${plural(n, 'tag, list, segment or campaign')} labelled with it ${n === 1 ? 'loses' : 'lose'} the label; nothing else is deleted.` : 'Nothing is labelled with it yet.',
        count === PROJECTS_MIN_ACTIVE ? ' With one project left, project labels and the project selector are switched off.' : '',
      ].join(''),
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => remove.mutateAsync(p.id).then(() => message.success('Project deleted')).catch((e) => message.error(errorMessage(e))),
    });
  };

  return (
    <div className="page">
      <PageHeader
        title="Projects"
        subtitle="The brands or businesses you use this CRM for. Label tags, lists, segments and campaigns with a project, then pick a project at the top of the page to see only its data."
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            New project
          </Button>
        }
      />
      {projects.data && count > 0 && count < PROJECTS_MIN_ACTIVE && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Project features turn on with ${PROJECTS_MIN_ACTIVE} or more projects`}
          description="With a single project everything already belongs to it, so project labels and the project selector stay hidden. Add another project to use them."
        />
      )}
      <Card>
        <Table<ProjectItem>
          rowKey="id"
          loading={projects.isLoading}
          locale={tableLocale(projects, <EmptyState description="No projects yet. Add each brand or business you run through this CRM." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>New project</Button>} />)}
          dataSource={projects.data ?? []}
          pagination={false}
          scroll={{ x: 'max-content' }}
          columns={[
            {
              title: 'Project',
              dataIndex: 'name',
              render: (v, p) => (
                <a onClick={() => open(p)} style={{ display: 'block' }}>
                  <NameCell name={p.acronym ? `${v} (${p.acronym})` : v} image={fileUrl(p.iconFileId ?? p.logoFileId)} square icon={p.iconFileId || p.logoFileId ? undefined : <AppstoreOutlined />} />
                </a>
              ),
            },
            { title: 'Colours', key: 'colors', render: (_, p) => <ColorSwatches project={p} /> },
            {
              title: 'Website',
              dataIndex: 'url',
              render: (v: string | null) =>
                v ? (
                  <Typography.Link href={v} target="_blank" rel="noreferrer">
                    {v.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </Typography.Link>
                ) : (
                  <Typography.Text type="secondary">None</Typography.Text>
                ),
            },
            { title: 'Owners', dataIndex: 'owners', render: (owners: ProjectOwnerItem[]) => <Owners owners={owners} /> },
            {
              title: 'Labelled',
              key: 'counts',
              render: (_, p) =>
                labelledCount(p) ? (
                  <Typography.Text type="secondary">
                    {[plural(p.counts.tags, 'tag'), plural(p.counts.lists, 'list'), plural(p.counts.segments, 'segment'), plural(p.counts.campaigns, 'campaign')].filter((s) => !s.startsWith('0 ')).join(' · ')}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">Nothing yet</Typography.Text>
                ),
            },
            {
              key: 'actions',
              align: 'right',
              width: 60,
              render: (_, p) => (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      { key: 'edit', label: 'Edit', onClick: () => open(p) },
                      { key: 'delete', label: 'Delete', danger: true, onClick: () => confirmDelete(p) },
                    ],
                  }}
                >
                  <Button type="text" icon={<MoreOutlined />} aria-label={`Actions for ${p.name}`} />
                </Dropdown>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editing === 'new' ? 'New project' : 'Edit project'}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        okText={editing === 'new' ? 'Create' : 'Save'}
        confirmLoading={create.isPending || update.isPending || (editing === 'new' && image.isPending)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={save} requiredMark={false}>
          <Form.Item label="Full logo" extra="Your logo in its own shape, for example a wordmark. Shown at the top of campaign emails for this project.">
            <ImageField kind="logo" fileId={current?.logoFileId ?? null} pending={pending.logo} busy={image.isPending && image.variables?.kind === 'logo'} onPick={(f) => void pickImage('logo', f)} />
          </Form.Item>
          <Form.Item label="Icon" extra="A square mark shown in the project selector and on project labels. Without one, the full logo is used.">
            <ImageField kind="icon" fileId={current?.iconFileId ?? null} pending={pending.icon} busy={image.isPending && image.variables?.kind === 'icon'} onPick={(f) => void pickImage('icon', f)} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} sm={16}>
              <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Give the project a name' }]}>
                <Input autoFocus maxLength={120} placeholder="StartupCon" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="acronym" label="Acronym" rules={[{ max: 20 }]} tooltip="Short form shown on project labels across the app. Without one, labels show the full name.">
                <Input maxLength={20} placeholder="SCon" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="url" label="Website" rules={[{ max: 500 }]}>
            <Input placeholder="https://example.com" />
          </Form.Item>
          <Form.Item name="description" label="Short description" rules={[{ max: 500 }]}>
            <MentionTextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="ownerIds" label="Owners" extra="Contacts who own or run this project.">
            <OwnersSelect initial={current?.owners ?? []} />
          </Form.Item>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Brand colours
          </Typography.Text>
          <Row gutter={12}>
            {BRAND_COLORS.map((c) => (
              <Col xs={24} sm={8} key={c.key}>
                <Form.Item name={c.key} label={c.label} rules={[hexRule]} normalize={normalizeHex}>
                  <HexColorInput label={`${c.label} colour`} />
                </Form.Item>
              </Col>
            ))}
          </Row>
          <GradientFields />
        </Form>
      </Modal>
    </div>
  );
}

/** Hex text box with a picker swatch in front; empty means no colour. */
function HexColorInput({ value, onChange, label }: { value?: string | null; onChange?: (v: string | null) => void; label: string }) {
  const valid = !!value && HEX.test(value);
  return (
    <Input
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value || null)}
      placeholder="#000000"
      maxLength={7}
      allowClear
      aria-label={label}
      prefix={
        <ColorPicker
          size="small"
          format="hex"
          disabledAlpha
          value={valid ? value : undefined}
          onChangeComplete={(c) => onChange?.(c.toHexString())}
          aria-label={`Pick ${label.toLowerCase()}`}
        />
      }
    />
  );
}

/** Gradient start and end colours with a live preview; both or neither. */
function GradientFields() {
  const form = Form.useFormInstance<ProjectForm>();
  const from = Form.useWatch('gradientFrom', form);
  const to = Form.useWatch('gradientTo', form);
  const ok = (v: string | null | undefined): v is string => !!v && HEX.test(v);
  const bothOrNeither = (other: 'gradientFrom' | 'gradientTo') => ({
    validator: (_: unknown, v: string | null | undefined) => (!v === !form.getFieldValue(other) ? Promise.resolve() : Promise.reject(new Error('Set both ends of the gradient, or neither'))),
  });
  return (
    <Form.Item label="Gradient" extra="From one colour to another." style={{ marginBottom: 0 }}>
      <Row gutter={12} align="top">
        <Col xs={24} sm={8}>
          <Form.Item name="gradientFrom" rules={[hexRule, bothOrNeither('gradientTo')]} dependencies={['gradientTo']} normalize={normalizeHex}>
            <HexColorInput label="Gradient start colour" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="gradientTo" rules={[hexRule, bothOrNeither('gradientFrom')]} dependencies={['gradientFrom']} normalize={normalizeHex}>
            <HexColorInput label="Gradient end colour" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <div
            aria-label="Gradient preview"
            style={{
              height: 32,
              borderRadius: 8,
              border: `1px solid ${palette.borderStrong}`,
              background: ok(from) && ok(to) ? `linear-gradient(90deg, ${from}, ${to})` : palette.canvas,
            }}
          />
        </Col>
      </Row>
    </Form.Item>
  );
}

/** The project's brand colours and gradient as small chips in the table. */
function ColorSwatches({ project: p }: { project: ProjectItem }) {
  const chips = BRAND_COLORS.filter((c) => p[c.key]);
  if (!chips.length && !p.gradientFrom) return <Typography.Text type="secondary">None</Typography.Text>;
  const chip = { width: 18, height: 18, borderRadius: 4, border: `1px solid ${palette.borderStrong}`, display: 'inline-block' } as const;
  return (
    <Space size={4}>
      {chips.map((c) => (
        <Tooltip key={c.key} title={`${c.label} ${p[c.key]}`}>
          <span style={{ ...chip, background: p[c.key]! }} />
        </Tooltip>
      ))}
      {p.gradientFrom && p.gradientTo && (
        <Tooltip title={`Gradient ${p.gradientFrom} → ${p.gradientTo}`}>
          <span style={{ ...chip, width: 40, background: `linear-gradient(90deg, ${p.gradientFrom}, ${p.gradientTo})` }} />
        </Tooltip>
      )}
    </Space>
  );
}

function Owners({ owners }: { owners: ProjectOwnerItem[] }) {
  if (!owners.length) return <Typography.Text type="secondary">None</Typography.Text>;
  return (
    <Space size={4} wrap>
      {owners.map((o, i) => (
        <span key={o.id}>
          <Link to={`/contacts/${o.id}`}>{contactName(o)}</Link>
          {i < owners.length - 1 ? ',' : ''}
        </span>
      ))}
    </Space>
  );
}

/** Upload / replace / remove the full logo (wide preview) or the icon (square preview). */
function ImageField({ kind, fileId, pending, busy, onPick }: { kind: ProjectImage; fileId: string | null; pending: File | undefined; busy: boolean; onPick: (file: File | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const pendingSrc = useMemo(() => (pending ? URL.createObjectURL(pending) : undefined), [pending]);
  useEffect(
    () => () => {
      if (pendingSrc) URL.revokeObjectURL(pendingSrc);
    },
    [pendingSrc],
  );
  const src = pendingSrc ?? fileUrl(fileId);
  const noun = kind === 'logo' ? 'logo' : 'icon';
  const box = { height: 56, width: kind === 'logo' ? 168 : 56, borderRadius: 8, border: `1px solid ${palette.borderStrong}`, flex: 'none' } as const;
  return (
    <Space size={12} align="center">
      <input
        ref={input}
        type="file"
        accept={IMAGE_ACCEPT}
        style={{ display: 'none' }}
        aria-label={`Project ${noun} file`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onPick(file);
        }}
      />
      {src ? (
        <img src={src} alt={`Project ${noun}`} style={{ ...box, objectFit: 'contain', background: '#fff', padding: 4 }} />
      ) : (
        <div style={{ ...box, display: 'grid', placeItems: 'center', background: palette.primarySoft, color: palette.primary, fontSize: 20 }}>
          <AppstoreOutlined />
        </div>
      )}
      <Button icon={<UploadOutlined />} loading={busy} onClick={() => input.current?.click()}>
        {src ? 'Replace' : 'Upload'}
      </Button>
      {src && (
        <Tooltip title={`Remove ${noun}`}>
          <Button danger icon={<DeleteOutlined />} aria-label={`Remove ${noun}`} disabled={busy} onClick={() => onPick(null)} />
        </Tooltip>
      )}
    </Space>
  );
}

/** Searchable multi-contact picker for project owners. */
function OwnersSelect({ value, onChange, initial }: { value?: string[]; onChange?: (v: string[]) => void; initial: ProjectOwnerItem[] }) {
  const toOption = (c: ProjectOwnerItem | ContactListItem) => ({ value: c.id, label: `${contactName(c)}${c.email ? ` <${c.email}>` : ''}` });
  const [known, setKnown] = useState(() => initial.map(toOption));
  const [found, setFound] = useState<{ value: string; label: string }[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const timer = useRef(0);

  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get<{ items: ContactListItem[] }>('/contacts', { params: { q: search, pageSize: 15, sortBy: 'last_name', sortDir: 'asc' } });
        setFound(r.data.items.map(toOption));
      } catch {
        setFound([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timer.current);
  }, [search]);

  const selected = (value ?? []).map((id) => known.find((o) => o.value === id) ?? found.find((o) => o.value === id) ?? { value: id, label: id });
  const options = [...selected, ...found.filter((o) => !value?.includes(o.value))];
  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          style={{ flex: 1, minWidth: 0 }}
          mode="multiple"
          showSearch
          filterOption={false}
          placeholder="Search contacts"
          value={value ?? []}
          onSearch={setSearch}
          onChange={(ids: string[]) => {
            setKnown((k) => [...k, ...found.filter((o) => ids.includes(o.value) && !k.some((x) => x.value === o.value))]);
            setSearch('');
            onChange?.(ids);
          }}
          loading={loading}
          notFoundContent={loading ? <Spin size="small" /> : search ? 'No matching contacts' : 'No contacts'}
          options={options}
        />
        <Tooltip title="New contact">
          <Button icon={<PlusOutlined />} aria-label="New contact" onClick={() => setCreating(true)} />
        </Tooltip>
      </Space.Compact>
      <QuickCreateModal
        objectType="CONTACT"
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(c) => {
          setKnown((k) => [...k, toOption(c)]);
          onChange?.([...(value ?? []), c.id]);
        }}
      />
    </>
  );
}
